import Box from "@mui/material/Box";
import { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { themeOptions } from "../../../app/theme";
import FileReaderComponent from "../../../components/FileReaderComponent";
import getUser from "../../../utils/getUser";
import { notify } from "../../public/components/Toast";
import LoaderWithDescription from "../../../components/LoaderWithDescription";

const baseIframeUrl = `/api/public/v1/redirect-manager/form?no-cache=${new Date().getTime()}`;

export interface FrameFormProps
  extends React.ComponentPropsWithoutRef<"iframe"> {
  component?: string; // my custom prop
  formData?: unknown;
  processId: string;
  type?: string;
  taskId?: string;
  callback: (
    data: Record<string, never>,
    processId: string,
    taskId?: string,
    reloadIframeView?: () => void,
  ) => void;
  callbackOtp?: (
    data: Record<string, never>,
    processId: string,
    taskId?: string,
    reloadIframeView?: () => void,
  ) => void;
  cancel: () => void;
  conclusionButtonText?: string;
  isInitial?: boolean;
}

interface DocumentProps {
  fileName: string;
  fileUrl: string;
  type: string;
}

const IframeForm = forwardRef<HTMLIFrameElement, FrameFormProps>(
  (props, ref) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isViewIframe, setIsViewIframe] = useState(true);
    const [iframeHeight, setIframeHeight] = useState<string>("99%");
    const [document, setDocument] = useState<null | DocumentProps>(null);

    const { i18n } = useTranslation();
    const currentLang = i18n.language;
    const iframeUrl = baseIframeUrl.includes("?")
      ? `${baseIframeUrl}&lang=${currentLang}`
      : `${baseIframeUrl}?lang=${currentLang}`;

    function reloadIframeView(){
      setIsLoading(false);
      setIsViewIframe(true);
    }

    // Tipando o evento message
    async function handleIframeEvent(
      event: MessageEvent<{
        message: string;
        document: null | DocumentProps;
        data: Record<string, never>;
        status: string;
        size: string;
        error: Error | null;
      }>
    ) {
      const data = event.data;

      if (data.message === "openDocument") {
        setDocument(data.document);
      }
      if (data.message === "submit") {
        setIsViewIframe(false);
        setIsLoading(true);
        props.callback(data.data, props.processId, props.taskId, reloadIframeView);
      }
      if (data.message === "resend-otp") {
        props.callbackOtp?.(data.data, props.processId, props.taskId);
      }
      if (data.status === "load_complete") {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsLoading(false);
      }
      if (data.size) {
        setIframeHeight(data.size);
      }
      if (data.message === "save") {
        notify("Atividade salva com sucesso!", "success", "");
        return;
      }
      if (data.message === "cancel") {
        props.cancel();
      }
    }

    useEffect(() => {
      window.addEventListener("message", handleIframeEvent);
      return () => {
        console.log("Desmontando Iframe...")
        window.removeEventListener("message", handleIframeEvent);
      };
    }, [props]);

    useEffect(() => {
      if (
        isLoading &&
        !(ref && "current" in ref ? ref.current : null) &&
        !props.formData
      )
        return;
      if (ref && "current" in ref && ref.current?.contentWindow) {
        ref.current.contentWindow.postMessage(
          { message: props.type, data: props.formData },
          "*"
        );
        ref.current.contentWindow.postMessage(
          {
            message: props.type,
            data: props.formData,
            conclusionButtonText:
              (
                props.formData as { conclusionButtonText?: string }
              )?.conclusionButtonText?.trim() === "defaultLabel"
                ? "Concluir"
                : ((props.formData as { conclusionButtonText?: string })
                    ?.conclusionButtonText ?? "Concluir"),
          },
          "*"
        );

        ref.current.contentWindow.postMessage(
          {
            message: "setup",
            data: {
              uploadFileUrl: `${window.location.origin}/api/v1/solicitacoes/upload`,
              getDocumentUrl: `${window.location.origin}/api/v1/files/{fileUid}/generate-temporary-link`,
              taskId: props.taskId || "",
              email: getUser()?.email,
              token: localStorage.getItem("token"),
              theme: themeOptions,
              dataListUrl: `${window.location.origin}/api/v1/data-list/{dataListId}/items/all`,
              processId: props.processId || "",
            },
          },
          "*"
        );
      }
    }, [ref, isLoading, props]);

    return (
      <>
        {isLoading && (
          <LoaderWithDescription
            title={
              props.isInitial
                ? "Iniciando a solicitação"
                : "Processando sua solicitação"
            }
            subtitle={
              props.isInitial
                ? "Por favor, aguarde enquanto o formulário é carregado"
                : "Estamos preparando a próxima etapa para você"
            }
          />
        )}
        {document !== null && !isLoading && (
          <FileReaderComponent
            fileName={document.fileName}
            fileUrl={document.fileUrl}
            handleClose={() => {
              setDocument(null);
            }}
            open
            type={document.type}
          />
        )}
        
        <Box
          component="iframe"
          ref={ref}
          scrolling="no"
          src={iframeUrl}
          sx={{
            width: "100%",
            height: iframeHeight,
            border: "none",
            overflow: "hidden",
            ...(!isViewIframe && { display: "none" }),
          }}
        />
        
      </>
    );
  }
);

IframeForm.displayName = "IframeForm";

export default IframeForm;
